import React, { useState, useEffect } from 'react';
import { Configuration, ConfigurationCategory, ConfigurationCategoryType, CONFIGURATION_CATEGORIES, CreateConfigurationRequest, UpdateConfigurationRequest } from '../types';
import configurationService from '../services/configurationService';
import ConfigurationList from '../components/ConfigurationList';
import ConfigurationForm from '../components/ConfigurationForm';
import { AlertCircle } from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import { useSettings } from '../../../hooks/useSettings';
import '../../../styles/ModulePage.css';
import '../styles/ConfigurationPage.css';

const ConfigurationPage: React.FC = () => {
  const { settings } = useSettings();
  // HIDDEN: Dark mode support - Keep code for future reuse
  const isDarkMode = false; // settings?.theme === 'dark';
  const [configurations, setConfigurations] = useState<ConfigurationCategory>({});
  const [selectedCategory, setSelectedCategory] = useState<ConfigurationCategoryType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingConfiguration, setEditingConfiguration] = useState<Configuration | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<'dropdown' | 'tabs'>('dropdown');

  // Load configurations on component mount
  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await configurationService.getAllConfigurations();
      
      if (result.success && result.data) {
        setConfigurations(result.data as ConfigurationCategory);
        // Auto-select first category if none selected
        if (!selectedCategory && Object.keys(result.data).length > 0) {
          setSelectedCategory(Object.keys(result.data)[0] as ConfigurationCategoryType);
        }
      } else {
        setError(result.message || 'Failed to load configurations');
      }
    } catch (error) {
      console.error('Error loading configurations:', error);
      setError('Failed to load configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (category: ConfigurationCategoryType) => {
    setSelectedCategory(category);
    setShowForm(false);
    setEditingConfiguration(null);
  };

  const handleAddNew = () => {
    setEditingConfiguration(null);
    setShowForm(true);
  };

  const handleEdit = (configuration: Configuration) => {
    setEditingConfiguration(configuration);
    setShowForm(true);
  };

  const handleDelete = async (category: ConfigurationCategoryType, key: string) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const result = await configurationService.deleteConfiguration(category, key);
      
      if (result.success) {
        setSuccess('Configuration deleted successfully');
        await loadConfigurations();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Failed to delete configuration');
      }
    } catch (error) {
      console.error('Error deleting configuration:', error);
      setError('Failed to delete configuration');
    }
  };

  const handleToggleStatus = async (category: ConfigurationCategoryType, key: string) => {
    try {
      const result = await configurationService.toggleConfigurationStatus(category, key);
      
      if (result.success) {
        setSuccess('Configuration status updated successfully');
        await loadConfigurations();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Failed to update configuration status');
      }
    } catch (error) {
      console.error('Error toggling configuration status:', error);
      setError('Failed to update configuration status');
    }
  };

  const handleSave = async (data: CreateConfigurationRequest | UpdateConfigurationRequest) => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      let result;
      if (editingConfiguration) {
        // Update existing configuration
        result = await configurationService.updateConfiguration(
          editingConfiguration.category as ConfigurationCategoryType,
          editingConfiguration.key,
          data as UpdateConfigurationRequest
        );
      } else {
        // Create new configuration
        result = await configurationService.createConfiguration(data as CreateConfigurationRequest);
      }
      
      if (result.success) {
        setSuccess(editingConfiguration ? 'Configuration updated successfully' : 'Configuration created successfully');
        setShowForm(false);
        setEditingConfiguration(null);
        await loadConfigurations();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError('Failed to save configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingConfiguration(null);
  };

  const getCategoryDisplayName = (category: string): string => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCategoryCount = (category: ConfigurationCategoryType): number => {
    return configurations[category]?.length || 0;
  };

  const getActiveCount = (category: ConfigurationCategoryType): number => {
    return configurations[category]?.filter(config => config.isActive).length || 0;
  };

  if (isLoading) {
    return (
      <div className={`module-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="module-container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            width: '100%'
          }}>
            <FadeLoader 
              color="#1976D2" 
              loading={true} 
              height={20} 
              width={6} 
              radius={3} 
              margin={3}
              speedMultiplier={0.8}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`module-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="module-container">
        <h1>Configuration Management</h1>
        <p>Manage system configurations, dropdown values, and application settings</p>

        {/* Message Display */}
        {success && (
          <div className="message success">
            {success}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="message error">
            {error}
          </div>
        )}

        {/* Configuration Layout */}
        <div className="configuration-layout">
          {/* Category Selection */}
          <div className="category-selection">
            <div className="category-header">
              <h2>Configuration Categories</h2>
              <div className="view-mode-toggle">
                <button
                  className={`toggle-btn ${viewMode === 'dropdown' ? 'active' : ''}`}
                  onClick={() => setViewMode('dropdown')}
                  title="Dropdown View"
                >
                  📋
                </button>
                <button
                  className={`toggle-btn ${viewMode === 'tabs' ? 'active' : ''}`}
                  onClick={() => setViewMode('tabs')}
                  title="Tabs View"
                >
                  📑
                </button>
              </div>
            </div>
            
            {viewMode === 'dropdown' ? (
              <div className="category-dropdown-container">
                <select
                  value={selectedCategory || ''}
                  onChange={(e) => handleCategorySelect(e.target.value as ConfigurationCategoryType)}
                  className="category-dropdown"
                >
                  <option value="">Select a category...</option>
                  {CONFIGURATION_CATEGORIES.map((category) => {
                    const count = getCategoryCount(category);
                    const activeCount = getActiveCount(category);
                    return (
                      <option key={category} value={category}>
                        {getCategoryDisplayName(category)} ({activeCount}/{count})
                      </option>
                    );
                  })}
                </select>
                {selectedCategory && (
                  <div className="category-info-compact">
                    <span className="category-name">{getCategoryDisplayName(selectedCategory)}</span>
                    <span className="category-stats">
                      {getActiveCount(selectedCategory)} of {getCategoryCount(selectedCategory)} active
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="category-tabs-container">
                <div className="category-tabs">
                  {CONFIGURATION_CATEGORIES.map((category) => {
                    const count = getCategoryCount(category);
                    const activeCount = getActiveCount(category);
                    const isSelected = selectedCategory === category;
                    
                    return (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className={`category-tab ${isSelected ? 'active' : ''}`}
                        title={`${getCategoryDisplayName(category)} - ${activeCount}/${count} active`}
                      >
                        <span className="tab-name">{getCategoryDisplayName(category)}</span>
                        <span className="tab-count">{activeCount}/{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="configuration-content">
        {!selectedCategory ? (
          <div className="empty-state">
            <AlertCircle className="empty-icon" />
            <h3>Select a Category</h3>
            <p>Choose a configuration category above to get started.</p>
          </div>
        ) : showForm ? (
          <div className="form-container">
            <div className="form-header">
              <h2>
                {editingConfiguration ? 'Edit Configuration' : 'Add New Configuration'}
              </h2>
              <p>
                {editingConfiguration 
                  ? 'Update the configuration details below.'
                  : 'Create a new configuration for the selected category.'
                }
              </p>
            </div>
            <ConfigurationForm
              configuration={editingConfiguration || undefined}
              onSave={handleSave}
              onCancel={handleCancel}
              isLoading={isSubmitting}
            />
          </div>
        ) : (
          <ConfigurationList
            configurations={configurations[selectedCategory] || []}
            category={selectedCategory}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggleStatus={handleToggleStatus}
            onAddNew={handleAddNew}
            isLoading={isLoading}
          />
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPage;
    